import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { managementDigestService } from '../services';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Plus, Trash2, Pencil, Send, History, ArrowLeft, Bell } from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const COMMON_TZ = ['Europe/London', 'UTC', 'Europe/Dublin', 'Europe/Paris', 'Asia/Dubai'];

function emptyForm(defaultRegion = 'UK') {
  return {
    name: '',
    is_active: true,
    frequency: 'weekly',
    send_hour: 7,
    send_minute: 0,
    day_of_week: 0,
    day_of_month: 1,
    recipient_mode: 'all_internal',
    recipient_user_ids: [],
    timezone: 'Europe/London',
    region: defaultRegion,
  };
}

function FrequencyBadge({ freq }) {
  const colours = { weekly: 'bg-blue-100 text-blue-700', monthly: 'bg-purple-100 text-purple-700' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colours[freq] || 'bg-gray-100 text-gray-600'}`}>
      {freq?.charAt(0).toUpperCase() + freq?.slice(1)}
    </span>
  );
}

function StatusBadge({ active }) {
  return active
    ? <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Active</span>
    : <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">Inactive</span>;
}

function formatSendTime(rule) {
  const h = String(rule.send_hour).padStart(2, '0');
  const m = String(rule.send_minute).padStart(2, '0');
  if (rule.frequency === 'weekly') {
    return `${WEEKDAYS[rule.day_of_week ?? 0]} at ${h}:${m}`;
  }
  if (rule.frequency === 'monthly') {
    const d = rule.day_of_month || 1;
    const suffix = d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th';
    return `${d}${suffix} of month at ${h}:${m}`;
  }
  return `${h}:${m}`;
}

function LogsDialog({ ruleId, ruleName, open, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !ruleId) return;
    setLoading(true);
    managementDigestService.getLogs(ruleId)
      .then(d => setLogs(d.logs || []))
      .finally(() => setLoading(false));
  }, [open, ruleId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send History — {ruleName}</DialogTitle>
          <DialogDescription>Last 20 digest runs for this rule.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin h-6 w-6 text-gray-400" /></div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No send history yet.</p>
        ) : (
          <div className="overflow-auto max-h-96">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Sent</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Sent / Failed</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Trigger</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                      {log.sent_at ? new Date(log.sent_at).toLocaleString('en-GB') : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        log.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>{log.status}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{log.recipients_sent} / {log.recipients_failed}</td>
                    <td className="px-3 py-2 text-gray-500">{log.triggered_manually ? 'Manual' : 'Auto'}</td>
                    <td className="px-3 py-2 text-red-600 max-w-xs truncate">{log.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RuleFormDialog({ open, onClose, editRule, onSaved, allRecipients, defaultRegion }) {
  const [form, setForm] = useState(emptyForm(defaultRegion));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editRule ? {
      name: editRule.name,
      is_active: editRule.is_active,
      frequency: editRule.frequency,
      send_hour: editRule.send_hour,
      send_minute: editRule.send_minute,
      day_of_week: editRule.day_of_week ?? 0,
      day_of_month: editRule.day_of_month ?? 1,
      recipient_mode: editRule.recipient_mode,
      recipient_user_ids: editRule.recipient_user_ids || [],
      timezone: editRule.timezone,
      region: editRule.region || defaultRegion,
    } : emptyForm(defaultRegion));
  }, [open, editRule, defaultRegion]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Please enter a name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        is_active: form.is_active,
        frequency: form.frequency,
        send_hour: Number(form.send_hour),
        send_minute: Number(form.send_minute),
        day_of_week: form.frequency === 'weekly' ? Number(form.day_of_week) : null,
        day_of_month: form.frequency === 'monthly' ? Number(form.day_of_month) : null,
        recipient_mode: form.recipient_mode,
        recipient_user_ids: form.recipient_mode === 'individual' ? form.recipient_user_ids : null,
        timezone: form.timezone,
        region: form.region,
      };
      if (editRule) {
        await managementDigestService.updateRule(editRule.id, payload);
        toast.success('Digest rule updated');
      } else {
        await managementDigestService.createRule(payload);
        toast.success('Digest rule created');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const toggleUserId = (id) => {
    const ids = form.recipient_user_ids || [];
    set('recipient_user_ids', ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editRule ? 'Edit Digest Rule' : 'New Management Digest Rule'}</DialogTitle>
          <DialogDescription>
            Configure when and to whom the weekly or monthly KPI snapshot email is sent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="dig-name">Name</Label>
            <Input
              id="dig-name"
              data-testid="digest-rule-name-input"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Weekly Management Digest"
              className="mt-1"
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch
              id="dig-active"
              checked={form.is_active}
              onCheckedChange={v => set('is_active', v)}
            />
            <Label htmlFor="dig-active">Active</Label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Region</Label>
              <Select value={form.region} onValueChange={v => set('region', v)}>
                <SelectTrigger data-testid="digest-region-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">UK (GBP £)</SelectItem>
                  <SelectItem value="UAE">UAE (AED)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={v => set('frequency', v)}>
                <SelectTrigger data-testid="digest-frequency-select" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Timezone</Label>
            <Select value={form.timezone} onValueChange={v => set('timezone', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMMON_TZ.map(tz => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.frequency === 'weekly' && (
            <div>
              <Label>Send on Day</Label>
              <Select value={String(form.day_of_week)} onValueChange={v => set('day_of_week', Number(v))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.frequency === 'monthly' && (
            <div>
              <Label>Day of Month</Label>
              <Select value={String(form.day_of_month)} onValueChange={v => set('day_of_month', Number(v))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Send Hour (0–23)</Label>
              <Input
                type="number" min={0} max={23}
                value={form.send_hour}
                onChange={e => set('send_hour', Number(e.target.value))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Send Minute (0–59)</Label>
              <Input
                type="number" min={0} max={59} step={5}
                value={form.send_minute}
                onChange={e => set('send_minute', Number(e.target.value))}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label>Recipients</Label>
            <Select value={form.recipient_mode} onValueChange={v => set('recipient_mode', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_internal">All Admins &amp; Schedulers</SelectItem>
                <SelectItem value="individual">Specific Users</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.recipient_mode === 'individual' && (
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
              {allRecipients.length === 0 ? (
                <p className="text-xs text-gray-400">No eligible recipients found.</p>
              ) : allRecipients.map(u => (
                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.recipient_user_ids.includes(u.id)}
                    onChange={() => toggleUserId(u.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span>{u.name}</span>
                  <span className="text-xs text-gray-400">({u.email})</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="digest-rule-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editRule ? 'Save Changes' : 'Create Rule'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ManagementDigestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { region: sessionRegion } = useSettings();
  const defaultRegion = sessionRegion === 'UAE' ? 'UAE' : 'UK';
  const [rules, setRules] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [logsRule, setLogsRule] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const isAdmin = user?.roles?.includes('admin');

  const loadRules = useCallback(async (signal) => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [rulesData, recipientsData] = await Promise.all([
        managementDigestService.listRules(signal),
        managementDigestService.listRecipients(signal),
      ]);
      if (signal?.aborted) return;
      setRules(rulesData.rules || []);
      setRecipients(recipientsData.users || []);
    } catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error('[ManagementDigestPage] loadRules error:', err);
      toast.error('Failed to load digest rules');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    const controller = new AbortController();
    loadRules(controller.signal);
    return () => controller.abort();
  }, [loadRules]);

  const handleSendNow = async (rule) => {
    setSendingId(rule.id);
    try {
      const result = await managementDigestService.sendNow(rule.id);
      toast.success(
        `Digest sent — ${result.recipients_sent} recipient(s). FTA: ${result.fta_rate}%`
      );
      loadRules();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete digest rule "${rule.name}"?`)) return;
    setDeletingId(rule.id);
    try {
      await managementDigestService.deleteRule(rule.id);
      toast.success('Rule deleted');
      loadRules();
    } catch {
      toast.error('Failed to delete rule');
    } finally {
      setDeletingId(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-gray-500">
        Admin access is required to manage digest rules.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/performance')}
            className="text-gray-600"
            data-testid="digest-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Performance Centre
          </Button>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Bell className="h-5 w-5 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900">Scheduled Insights</h1>
            </div>
            <p className="text-sm text-gray-500 max-w-xl">
              Automatically email a Business Pulse KPI snapshot to Admins and Schedulers on a
              weekly or monthly schedule. Configure one or more rules below.
            </p>
          </div>
          <Button
            data-testid="digest-add-rule-btn"
            onClick={() => { setEditRule(null); setFormOpen(true); }}
            className="shrink-0"
          >
            <Plus className="h-4 w-4 mr-1" /> New Rule
          </Button>
        </div>

        {/* What's included callout */}
        <div className="mb-6 p-4 rounded-lg border border-indigo-200 bg-indigo-50">
          <p className="text-sm text-indigo-800 font-medium mb-1">Each digest email includes:</p>
          <ul className="text-sm text-indigo-700 space-y-0.5 list-disc list-inside">
            <li>Forecast Revenue, Completed Jobs, Operational Backlog (month-to-date)</li>
            <li>Sites Overdue &amp; Billing at Risk</li>
            <li>First-Time Approval Rate (rolling 90 days)</li>
            <li>Attention Required flags (critical / warning)</li>
          </ul>
        </div>

        {/* Rules list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
            <Bell className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No digest rules yet</p>
            <p className="text-sm text-gray-400 mb-4">Create your first rule to start sending automated management digests.</p>
            <Button
              data-testid="digest-empty-add-btn"
              onClick={() => { setEditRule(null); setFormOpen(true); }}
            >
              <Plus className="h-4 w-4 mr-1" /> Create First Rule
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="digest-rules-list">
            {rules.map(rule => (
              <div
                key={rule.id}
                data-testid={`digest-rule-row-${rule.id}`}
                className="bg-white border rounded-lg p-4 flex items-start justify-between gap-4 shadow-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 truncate">{rule.name}</span>
                    <FrequencyBadge freq={rule.frequency} />
                    <StatusBadge active={rule.is_active} />
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      rule.region === 'UAE' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                    }`}>{rule.region || 'UK'}</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {formatSendTime(rule)} &bull; {rule.timezone} &bull;{' '}
                    {rule.recipient_mode === 'all_internal'
                      ? 'All Admins & Schedulers'
                      : `${(rule.recipient_user_ids || []).length} specific user(s)`}
                  </p>
                  {rule.last_sent_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Last sent: {new Date(rule.last_sent_at).toLocaleString('en-GB')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSendNow(rule)}
                    disabled={sendingId === rule.id}
                    title="Send now (test)"
                    data-testid={`digest-send-now-btn-${rule.id}`}
                  >
                    {sendingId === rule.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Send className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLogsRule(rule)}
                    title="View send history"
                    data-testid={`digest-logs-btn-${rule.id}`}
                  >
                    <History className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setEditRule(rule); setFormOpen(true); }}
                    title="Edit rule"
                    data-testid={`digest-edit-btn-${rule.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(rule)}
                    disabled={deletingId === rule.id}
                    title="Delete rule"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`digest-delete-btn-${rule.id}`}
                  >
                    {deletingId === rule.id
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* APScheduler note */}
        <div className="mt-8 p-3 rounded-lg bg-gray-100 border border-gray-200">
          <p className="text-xs text-gray-500">
            <strong>Scheduling:</strong> Rules are checked every 30 minutes. A digest will send
            once per period (weekly/monthly) at the configured time in the rule's timezone.
            If a send fails, it will retry automatically on the next 30-minute tick.
          </p>
        </div>
      </div>

      <RuleFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editRule={editRule}
        onSaved={() => { setFormOpen(false); loadRules(); }}
        allRecipients={recipients}
        defaultRegion={defaultRegion}
      />

      <LogsDialog
        ruleId={logsRule?.id}
        ruleName={logsRule?.name}
        open={!!logsRule}
        onClose={() => setLogsRule(null)}
      />
    </div>
  );
}
